// In-memory mock implementation of every route in docs/API_CONTRACT.md.
// Used when VITE_USE_MOCK=true so the whole app is demoable via `npm run dev` with no backend.
// Faithfully simulates: theme selection (validated the same way the real backend validates
// `themePreference`), the streak state machine, and deterministic per-day quote selection.

import { ALL_TRADITIONS, DEFAULT_THEME_ID, isValidTheme, TRADITION_PARENTS, type Tradition } from '../theme/tokens';
import type {
  AdminAnalytics,
  AdminUser,
  AdminUsersResponse,
  Book,
  CalendarEvent,
  CalendarResponse,
  CreateCheckoutResponse,
  CreateJournalEntryBody,
  DailyMoment,
  ExperienceLevel,
  JournalEntriesResponse,
  JournalEntry,
  LeaderboardEntry,
  LeaderboardResponse,
  LibraryDownload,
  LibraryResponse,
  Me,
  OnboardingIntent,
  PilgrimRank,
  Pillar,
  PillarsResponse,
  Practice,
  PracticeAccess,
  PracticeAudio,
  PracticeMilestone,
  PracticesResponse,
  PracticeType,
  PostSessionBody,
  PostSessionResponse,
  Quote,
  ReinforcementResponse,
  Routine,
  RoutineItem,
  ScheduleDay,
  ThirtyDaySchedule,
  Tier,
  TodayPlan,
  TodayQuotes,
  UpdateJournalEntryBody,
  UpdateMeBody,
  UpdateRoutineBody,
} from './types';

// Mirrors backend/app/domain.py's FREE_TIER_LEVEL_CAP / SHIELD_CAP -- kept in sync by hand, same
// pattern already used for TRADITION_PARENTS above.
const FREE_TIER_LEVEL_CAP = 5;
const SHIELD_CAP = 2;

// Mirrors backend/app/domain.py's ONBOARDING_INTENTS / EXPERIENCE_LEVELS / time-budget bounds.
const ONBOARDING_INTENTS: OnboardingIntent[] = ['deepen', 'explore', 'routine'];
const EXPERIENCE_LEVELS: ExperienceLevel[] = ['new', 'some', 'experienced'];
const MIN_TIME_BUDGET_MINUTES = 3;
const MAX_TIME_BUDGET_MINUTES = 120;

// Mirrors backend/app/domain.py's DAILY_MOMENTS / MAX_ROUTINE_PRACTICES.
const DAILY_MOMENTS: DailyMoment[] = ['morning', 'midday', 'evening', 'night'];
const MAX_ROUTINE_PRACTICES = 20;

// Mirrors backend/app/handlers/user.py's JOURNAL_MAX_TEXT_LENGTH.
const JOURNAL_MAX_TEXT_LENGTH = 20_000;

// Mirrors backend/app/handlers/user.py's PILGRIM_RANKS -- ascending (threshold, title) pairs.
const PILGRIM_RANKS: Array<[number, PilgrimRank]> = [
  [0, 'Seeker'],
  [10, 'Practitioner'],
  [30, 'Adherent'],
  [75, 'Adept'],
  [150, 'Elder'],
  [300, 'Sage'],
  [600, 'Luminary'],
];

function computePilgrimRank(totalCompletions: number): PilgrimRank {
  let title: PilgrimRank = PILGRIM_RANKS[0][1];
  for (const [threshold, name] of PILGRIM_RANKS) {
    if (totalCompletions >= threshold) title = name;
  }
  return title;
}

// Mirrors backend/app/handlers/user.py's PRACTICE_MILESTONES.
const PRACTICE_MILESTONES: PracticeMilestone[] = [7, 30, 100, 365];

function computePracticeMilestone(totalPracticeDays: number): PracticeMilestone {
  let milestone: PracticeMilestone = 0;
  for (const m of PRACTICE_MILESTONES) {
    if (totalPracticeDays >= m) milestone = m;
  }
  return milestone;
}

function todayUtcDateString(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10);
}

function daysBetween(a: string, b: string): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  const da = Date.parse(`${a}T00:00:00Z`);
  const db = Date.parse(`${b}T00:00:00Z`);
  return Math.round((db - da) / msPerDay);
}

// --- Seed content -----------------------------------------------------------------------------

const QUOTES: Quote[] = [
  { quoteId: 'q_001', text: 'Peace comes from within. Do not seek it without.', author: 'Buddha', tradition: 'buddhist' },
  { quoteId: 'q_002', text: 'You have the right to work, but never to the fruit of work.', author: 'Bhagavad Gita', tradition: 'hindu' },
  { quoteId: 'q_003', text: 'Do to others as you would have them do to you.', author: 'Jesus, Luke 6:31', tradition: 'christian' },
  { quoteId: 'q_004', text: 'None of you truly believes until he wishes for his brother what he wishes for himself.', author: 'Prophet Muhammad', tradition: 'islamic' },
  { quoteId: 'q_005', text: 'Whoever saves a single life, it is as if he had saved an entire world.', author: 'The Talmud', tradition: 'jewish' },
  { quoteId: 'q_006', text: 'You have power over your mind, not outside events. Realize this, and you will find strength.', author: 'Marcus Aurelius', tradition: 'stoic' },
  { quoteId: 'q_008', text: 'A journey of a thousand miles begins with a single step.', author: 'Lao Tzu', tradition: 'taoist' },
];

// premiumLocked is computed at read time (see withPremiumLocked below), not stored here --
// mirrors the real backend never persisting a per-caller-tier field on the content row itself.
type SeedPractice = Omit<
  Practice,
  | 'transcript'
  | 'premiumLocked'
  | 'access'
  | 'contentVersion'
  | 'circle'
  | 'publicationState'
  | 'reviewerStatus'
  | 'reviewerName'
  | 'sourceRefs'
  | 'outcome'
  | 'guidanceRequired'
> & Partial<Pick<Practice, 'publicationState'>> & { script?: string };

const PRACTICES: SeedPractice[] = [
  { practiceId: 'p_001', title: 'Breath Awareness Meditation', type: 'meditation', tradition: 'buddhist', durationSeconds: 600, description: 'A classic mindfulness-of-breathing practice to settle the mind.' },
  { practiceId: 'p_002', title: 'Om Chanting Meditation', type: 'meditation', tradition: 'hindu', durationSeconds: 480, description: 'Resonant chanting to still the mind and open the heart.' },
  { practiceId: 'p_003', title: 'The Lord’s Prayer, Slowly', type: 'prayer', tradition: 'christian', durationSeconds: 300, description: 'A contemplative, unhurried recitation with pauses for reflection.' },
  { practiceId: 'p_004', title: 'Salah Reflection', type: 'prayer', tradition: 'islamic', durationSeconds: 420, description: 'A guided reflection to prepare the heart before prayer.' },
  { practiceId: 'p_005', title: 'Shema Contemplation', type: 'prayer', tradition: 'jewish', durationSeconds: 360, description: 'A slow, meditative reading of the Shema and its meaning.' },
  { practiceId: 'p_006', title: 'Stoic Morning Reflection', type: 'meditation', tradition: 'stoic', durationSeconds: 300, description: 'A guided review of what is and isn’t within your control today.' },
  { practiceId: 'p_008', title: 'Wu Wei Stillness', type: 'meditation', tradition: 'taoist', durationSeconds: 600, description: 'Effortless action: a practice in letting go and flowing with the moment.' },
  { practiceId: 'p_buddhist_reading_dhammapada', title: 'A Verse from the Dhammapada', type: 'reading', tradition: 'buddhist', durationSeconds: 300, description: 'A single verse from the Dhammapada, read slowly with pauses to let it settle.' },
  { practiceId: 'p_buddhist_service_dana', title: 'Dana: A Reflection on Generosity', type: 'service', tradition: 'buddhist', durationSeconds: 240, description: 'A short reflection on dana (generosity) to carry into one real act of giving today.' },

  // Christianity's Prayer pillar -- the flagship, fully-built-out ladder (see
  // docs/PRACTICE_LEVELS.md). Levels 1-5 are free; 6-8 demonstrate the premium-locked state.
  { practiceId: 'p_christian_quiet_prayer', title: 'A Quiet Moment with God', type: 'prayer', tradition: 'christian', pillarId: 'christian_prayer', level: 1, durationSeconds: 480, description: 'A contemplative prayer practice for resting in stillness and gratitude.', script: "Find a comfortable, quiet place to sit... Let your hands rest gently, and allow your breathing to settle... There is nowhere else you need to be right now. Simply be present, here, in this stillness... When you're ready, silently offer this moment as it is, without needing to fill it with words..." },
  { practiceId: 'p_christian_prayer_lords_prayer', title: "The Lord's Prayer, Phrase by Phrase", type: 'prayer', tradition: 'christian', pillarId: 'christian_prayer', level: 2, durationSeconds: 360, description: 'The prayer Jesus taught his disciples, prayed slowly, one phrase at a time.' },
  { practiceId: 'p_christian_prayer_morning_offering', title: 'A Morning Offering', type: 'prayer', tradition: 'christian', pillarId: 'christian_prayer', level: 3, durationSeconds: 300, description: 'A brief practice of offering the whole day to God before it begins.' },
  { practiceId: 'p_christian_prayer_intercession', title: 'Interceding for Someone Else', type: 'prayer', tradition: 'christian', pillarId: 'christian_prayer', level: 4, durationSeconds: 360, description: 'A practice of praying by name for one specific person.' },
  { practiceId: 'p_christian_prayer_examen', title: 'The Examen: A Review of the Day', type: 'prayer', tradition: 'christian', pillarId: 'christian_prayer', level: 5, durationSeconds: 480, description: "A gentle end-of-day prayer of reviewing where God was present, in Ignatius of Loyola's Examen." },
  { practiceId: 'p_christian_prayer_rosary_decade', title: 'Praying the Rosary: Your First Decade', type: 'prayer', tradition: 'christian', pillarId: 'christian_prayer', level: 6, durationSeconds: 540, description: "An introduction to the Rosary's rhythm, one decade held around a single mystery." },
  { practiceId: 'p_christian_prayer_jesus_prayer', title: 'The Jesus Prayer: Contemplative Repetition', type: 'prayer', tradition: 'christian', pillarId: 'christian_prayer', level: 7, durationSeconds: 480, description: 'The ancient hesychast practice of repeating the Jesus Prayer in rhythm with the breath.' },
  { practiceId: 'p_christian_prayer_for_enemies', title: 'Praying for Those Who Are Hard to Love', type: 'prayer', tradition: 'christian', pillarId: 'christian_prayer', level: 8, durationSeconds: 420, description: 'Praying by name for someone who has genuinely wronged you.' },

  { practiceId: 'p_christian_reading_psalm23', title: 'Psalm 23, Slowly', type: 'reading', tradition: 'christian', pillarId: 'christian_scripture', level: 1, durationSeconds: 300, description: 'Psalm 23 read slowly, phrase by phrase, with room to sit with each line.' },
  { practiceId: 'p_christian_stillness_centering_prayer', title: 'Centering Prayer: A Sacred Word', type: 'meditation', tradition: 'christian', pillarId: 'christian_stillness', level: 1, durationSeconds: 480, description: "An introduction to centering prayer, resting silently in God's presence." },
  { practiceId: 'p_christian_service_neighbor', title: 'Loving Your Neighbor Today', type: 'service', tradition: 'christian', pillarId: 'christian_mercy', level: 1, durationSeconds: 240, description: "A short reflection on 'love thy neighbor' to carry into one concrete act today." },
];

const PILLARS: Pillar[] = [
  { pillarId: 'christian_prayer', tradition: 'christian', practiceType: 'prayer', title: 'Prayer', description: 'From a first quiet moment with God to interceding for those who are hard to love.', order: 1 },
  { pillarId: 'christian_scripture', tradition: 'christian', practiceType: 'reading', title: 'Scripture', description: 'Reading the Bible slowly, a phrase at a time.', order: 2 },
  { pillarId: 'christian_stillness', tradition: 'christian', practiceType: 'meditation', title: 'Stillness', description: "Contemplative practice, resting silently in God's presence.", order: 3 },
  { pillarId: 'christian_mercy', tradition: 'christian', practiceType: 'service', title: 'Mercy', description: 'Turning reflection into a concrete act of love toward one real person, today.', order: 4 },
];

// Mirrors backend/app/content/seed/calendar_events.json -- kept in sync by hand, including
// region/community/startTime (real, individually researched and cited per event -- see
// docs/CUSTOM_DOMAIN.md's sibling doc, the calendar-richness research pass in the Decision Log).
// `movable` flags a lunar/solar-shifting date (needs annual re-seeding) vs. a fixed one like
// Christmas. Raw seed shape, before uncertainty/relatedPillarId are computed at read time (same
// "compute at shape time, don't hand-edit every row" pattern as withAccessState below).
type SeedCalendarEvent = Omit<CalendarEvent, 'uncertainty' | 'relatedPillarId'>;

/** Mirrors content.py's _shape_calendar_event: uncertainty defaults to "confirmed" (every
 * current event's date was web-search-verified at authoring time -- see
 * app.domain.DEFAULT_CALENDAR_UNCERTAINTY), relatedPillarId is computed from each tradition's
 * flagship (order-1) pillar. region/community/startTime are real per-event data on the seed row
 * itself now (see CALENDAR_EVENTS below), not computed here -- most were null until this pass. */
function withCalendarRichness(e: SeedCalendarEvent): CalendarEvent {
  const flagship = PILLARS.filter((p) => p.tradition === e.tradition).sort((a, b) => a.order - b.order)[0];
  return {
    ...e,
    uncertainty: 'confirmed',
    relatedPillarId: flagship?.pillarId ?? null,
  };
}

const CALENDAR_EVENTS: SeedCalendarEvent[] = [
  { eventId: 'cal_christian_epiphany_2026', tradition: 'christian', title: 'Epiphany', description: 'Commemorates the manifestation of Christ to the Magi — the visit of the wise men, and in Eastern traditions, Christ’s baptism.', date: '2026-01-06', movable: false, region: 'Global, especially significant in Western Europe (Spain, Italy, France, Germany, Poland) and Latin America, where it is celebrated as Three Kings Day (Dia de los Reyes)', community: 'Western Christianity (Catholic and Protestant, Gregorian calendar) observes Epiphany on Jan 6. Eastern Orthodox Christianity (Julian calendar) observes the equivalent feast, Theophany, on Jan 19 (Gregorian) -- a different date.', startTime: null },
  { eventId: 'cal_christian_ash_wednesday_2026', tradition: 'christian', title: 'Ash Wednesday', description: 'The start of Lent, marked by the imposition of ashes as a sign of repentance and mortality.', date: '2026-02-18', movable: true, region: 'Global, wherever Western Christianity is practiced', community: 'Observed only in Western Christianity (Catholic and Protestant, Gregorian calendar). Eastern Orthodox Christianity has no Ash Wednesday -- its Great Lent instead begins on Clean Monday (Feb 23, 2026), uses no ashes, and counts Sundays within its 40 days.', startTime: null },
  { eventId: 'cal_christian_palm_sunday_2026', tradition: 'christian', title: 'Palm Sunday', description: 'Commemorates Jesus’s triumphal entry into Jerusalem, marking the start of Holy Week.', date: '2026-03-29', movable: true, region: 'Global Western Christianity', community: 'This date (Mar 29, 2026) is the Western Christianity observance. Eastern Orthodox Christianity observes Palm Sunday on Apr 5, 2026 -- one week later -- per its Julian-calendar-based Pascha reckoning.', startTime: null },
  { eventId: 'cal_christian_good_friday_2026', tradition: 'christian', title: 'Good Friday', description: 'Commemorates the crucifixion of Jesus — the most solemn day of the Christian year.', date: '2026-04-03', movable: true, region: 'Global Western Christianity', community: 'This date (Apr 3, 2026) is the Western Christianity observance. Eastern Orthodox Christianity observes Good Friday on Apr 10, 2026 -- one week later -- per its Julian-calendar-based Pascha reckoning.', startTime: null },
  { eventId: 'cal_christian_easter_2026', tradition: 'christian', title: 'Easter Sunday', description: 'The resurrection of Jesus Christ — the central feast of the Christian calendar.', date: '2026-04-05', movable: true, region: 'Global', community: 'This date (Apr 5, 2026) is Western Christianity’s Easter (Catholic and Protestant, Gregorian calendar). Eastern Orthodox Christianity celebrates Easter/Pascha on Apr 12, 2026 -- one week later this year -- per its Julian-calendar-based reckoning.', startTime: 'For liturgical traditions that hold an Easter Vigil (Catholic, Anglican, Lutheran), the observance can be marked from after sunset on Holy Saturday (Apr 4, 2026); many other Protestant traditions hold only a Sunday-morning service, so there is no single universal start time.' },
  { eventId: 'cal_christian_ascension_2026', tradition: 'christian', title: 'Ascension', description: 'Commemorates Christ’s ascension into heaven, forty days after Easter.', date: '2026-05-14', movable: true, region: 'Global Western Christianity', community: 'This date (May 14, 2026, 40 days after Western Easter) is the Western Christianity observance. Eastern Orthodox Christianity observes Ascension on May 21, 2026 -- 40 days after Orthodox Pascha.', startTime: null },
  { eventId: 'cal_christian_pentecost_2026', tradition: 'christian', title: 'Pentecost', description: 'Commemorates the descent of the Holy Spirit on the apostles, fifty days after Easter — often called the birthday of the Church.', date: '2026-05-24', movable: true, region: 'Global Western Christianity', community: 'This date (May 24, 2026, 50 days after Western Easter) is the Western Christianity observance. Eastern Orthodox Christianity observes Pentecost on May 31, 2026 -- 50 days after Orthodox Pascha.', startTime: null },
  { eventId: 'cal_christian_advent_2026', tradition: 'christian', title: 'First Sunday of Advent', description: 'The start of Advent, the season of preparation and anticipation before Christmas.', date: '2026-11-29', movable: true, region: 'Global Western Christianity', community: 'The four-Sunday Advent season is a Western Christian (Catholic and Protestant) practice. Eastern Orthodox Christianity’s parallel preparatory season is the Nativity Fast, a 40-day ascetic fast beginning November 15 (or Nov 28 on the old Julian calendar), with a different theological focus and no First Sunday structure.', startTime: null },
  { eventId: 'cal_christian_christmas_2026', tradition: 'christian', title: 'Christmas', description: 'Commemorates the birth of Jesus Christ.', date: '2026-12-25', movable: false, region: 'Global', community: 'December 25 is kept by Western Christianity (Catholic and Protestant, Gregorian calendar) and by Orthodox churches on the Revised Julian/Gregorian calendar (e.g. Greek, Romanian, Bulgarian, Antiochian). Orthodox churches still on the old Julian calendar (Russian, Serbian, Georgian, Ukrainian, Jerusalem Patriarchate) plus Coptic Christians instead celebrate on January 7 (Gregorian).', startTime: 'Christmas Eve/Midnight Mass traditionally begins after sunset on Dec 24, but Christmas Day itself as a day-long observance has no single canonical start time -- customs vary by denomination and region.' },
  { eventId: 'cal_islamic_ramadan_2026', tradition: 'islamic', title: 'Ramadan Begins', description: 'The start of the month-long fast from dawn to sunset, one of the Five Pillars of Islam. Exact date confirmed by moon sighting.', date: '2026-02-18', movable: true, region: 'Global Muslim world, especially the Middle East, North Africa, South Asia, and Southeast Asia', community: 'Observed by Muslims worldwide across both Sunni and Shia traditions; the exact start can vary by a day between countries/communities depending on local moon-sighting vs. astronomical-calculation methodology.', startTime: 'Begins at sunset on the evening before (Feb 17, 2026), per the Islamic lunar calendar’s sunset-to-sunset day; the first fasting day itself starts at dawn (Fajr) on Feb 18.' },
  { eventId: 'cal_islamic_eid_fitr_2026', tradition: 'islamic', title: 'Eid al-Fitr', description: 'The Festival of Breaking the Fast, marking the end of Ramadan.', date: '2026-03-20', movable: true, region: 'Global Muslim world', community: 'Observed by Sunni and Shia Muslims worldwide; exact date depends on moon sighting and can vary by a day between countries.', startTime: 'Begins at sunset on the evening before (Mar 19, 2026), per the Islamic lunar calendar’s sunset-to-sunset day; the Eid prayer is performed the following morning.' },
  { eventId: 'cal_islamic_eid_adha_2026', tradition: 'islamic', title: 'Eid al-Adha', description: 'The Festival of Sacrifice, commemorating Ibrahim’s willingness to sacrifice his son, and marking the culmination of Hajj.', date: '2026-05-27', movable: true, region: 'Global Muslim world; culminates the annual Hajj pilgrimage centered on Mecca, Saudi Arabia', community: 'Observed by Sunni and Shia Muslims worldwide.', startTime: 'Begins at sunset on the evening before (May 26, 2026), per the Islamic lunar calendar’s sunset-to-sunset day.' },
  { eventId: 'cal_islamic_new_year_2026', tradition: 'islamic', title: 'Islamic New Year', description: 'The first day of Muharram, marking the start of the Islamic year 1448 AH.', date: '2026-06-16', movable: true, region: 'Global Muslim world', community: 'Observed by both Sunni and Shia Muslims, marking the start of the Hijri calendar year and commemorating the Prophet Muhammad’s migration (Hijra) from Mecca to Medina.', startTime: 'Begins at sunset on the evening before (Jun 15, 2026), per the Islamic lunar calendar’s sunset-to-sunset day.' },
  { eventId: 'cal_islamic_ashura_2026', tradition: 'islamic', title: 'Ashura', description: 'The 10th of Muharram — a recommended fast day for Sunni Muslims, and a day of mourning for Shia Muslims commemorating Karbala.', date: '2026-06-25', movable: true, region: 'Global Muslim world; Shia observance is especially concentrated in Iran, Iraq (Karbala), Lebanon, Bahrain, and South Asia (India, Pakistan)', community: 'Observed very differently by the two branches: Sunni Muslims treat it as a recommended, voluntary fast commemorating God delivering Moses from Pharaoh. Shia Muslims observe it as their most solemn day of mourning, commemorating Imam Hussain’s martyrdom at Karbala (680 CE), marked by mourning gatherings and processions rather than fasting.', startTime: 'Begins at sunset on the evening before (Jun 24, 2026), per the Islamic lunar calendar’s sunset-to-sunset day.' },
  { eventId: 'cal_islamic_mawlid_2026', tradition: 'islamic', title: 'Mawlid al-Nabi', description: 'Commemorates the birth of the Prophet Muhammad — its observance is contested within Sunni Islam itself.', date: '2026-08-25', movable: true, region: 'Widely observed across the Middle East, North Africa, South Asia, and Southeast Asia; notably not an official public holiday in Saudi Arabia or Qatar', community: 'Celebrated by most Sunni schools (Hanafi, Maliki, Shafi’i) and by Shia Muslims. Salafi and Wahhabi Muslims reject it as an unsanctioned religious innovation (bid’ah) and do not observe it.', startTime: 'Begins at sunset on the evening before (Aug 24, 2026), per the Islamic lunar calendar’s sunset-to-sunset day.' },
  { eventId: 'cal_jewish_purim_2026', tradition: 'jewish', title: 'Purim', description: 'Commemorates the deliverance of the Jewish people in ancient Persia, as told in the Book of Esther.', date: '2026-03-02', movable: true, region: 'Global Jewish diaspora and Israel', community: 'Observed by Jewish communities worldwide (Orthodox, Conservative, Reform) on 14 Adar. Jerusalem and other cities held to have been walled at the time of Joshua observe Shushan Purim one day later, on 15 Adar (Mar 3, 2026).', startTime: 'Begins at sunset on the evening before (Mar 1, 2026), per the Hebrew calendar’s sunset-to-sunset day; the Megillah is traditionally read both that evening and the next morning.' },
  { eventId: 'cal_jewish_passover_2026', tradition: 'jewish', title: 'Passover (Pesach)', description: 'Commemorates the Exodus from Egypt — marked by the seder meal and the retelling of the liberation story.', date: '2026-04-02', movable: true, region: 'Global Jewish diaspora and Israel', community: 'Observed by Jewish communities worldwide. In Israel and Reform/Reconstructionist practice, Passover is 7 days; Orthodox and Conservative communities in the Diaspora observe 8 days, per the traditional second-day-Yom-Tov practice.', startTime: 'Begins at sunset on the evening before (Apr 1, 2026), per the Hebrew calendar’s sunset-to-sunset day -- marked by the Seder that evening.' },
  { eventId: 'cal_jewish_shavuot_2026', tradition: 'jewish', title: 'Shavuot', description: 'Commemorates the giving of the Torah at Mount Sinai, seven weeks after Passover.', date: '2026-05-21', movable: true, region: 'Global Jewish diaspora and Israel', community: 'Observed by Jewish communities worldwide. In Israel and Reform practice, Shavuot is a single day; Orthodox and Conservative Diaspora communities observe two days.', startTime: 'Begins at sunset on the evening before (May 20, 2026), per the Hebrew calendar’s sunset-to-sunset day.' },
  { eventId: 'cal_jewish_rosh_hashanah_2026', tradition: 'jewish', title: 'Rosh Hashanah', description: 'The Jewish New Year — a time of introspection and the sounding of the shofar.', date: '2026-09-11', movable: true, region: 'Global Jewish diaspora and Israel', community: 'Observed by Jewish communities worldwide to open the High Holy Days. Orthodox and Conservative communities keep it as two days even in Israel; some Reform congregations mark only one day.', startTime: 'Begins at sunset on the evening before (Sept 10, 2026), per the Hebrew calendar’s sunset-to-sunset day.' },
  { eventId: 'cal_jewish_yom_kippur_2026', tradition: 'jewish', title: 'Yom Kippur', description: 'The Day of Atonement — the holiest day of the Jewish year, marked by fasting and prayer.', date: '2026-09-20', movable: true, region: 'Global, with Jerusalem/Israel as the historic spiritual center', community: 'Observed by Jewish communities worldwide across all denominations as the holiest day of the year.', startTime: 'Begins at sunset on the evening before (Sept 19, 2026), marking the start of a roughly 25-hour fast, per the Hebrew calendar’s sunset-to-sunset day.' },
  { eventId: 'cal_jewish_sukkot_2026', tradition: 'jewish', title: 'Sukkot', description: 'The Feast of Booths, commemorating the Israelites’ wilderness dwellings.', date: '2026-09-26', movable: true, region: 'Global Jewish diaspora and Israel', community: 'Observed by Jewish communities worldwide; 7 days in Israel and Reform practice, 8 days (incorporating Shemini Atzeret) in Orthodox/Conservative Diaspora practice.', startTime: 'Begins at sunset on the evening before (Sept 25, 2026), per the Hebrew calendar’s sunset-to-sunset day.' },
  { eventId: 'cal_jewish_hanukkah_2026', tradition: 'jewish', title: 'Hanukkah', description: 'The Festival of Lights, commemorating the rededication of the Second Temple.', date: '2026-12-04', movable: true, region: 'Global Jewish diaspora and Israel', community: 'Observed by Jewish communities worldwide across all denominations; an 8-day festival with widely varying family and regional customs.', startTime: 'Begins at sunset on the evening before (Dec 3, 2026), per the Hebrew calendar’s sunset-to-sunset day, when the first candle is lit.' },
  { eventId: 'cal_hindu_holi_2026', tradition: 'hindu', title: 'Holi', description: 'The festival of colors, celebrating the arrival of spring and the triumph of good over evil.', date: '2026-03-03', movable: true, region: 'Originated in and most concentrated in North India, especially the Braj region (Mathura, Vrindavan, Barsana, Nandgaon) associated with Krishna; also celebrated across India, Nepal, and the Hindu diaspora (Fiji, Trinidad, Mauritius, UK, US)', community: 'A pan-Hindu festival celebrated across virtually all Hindu traditions; especially elaborate and devotionally significant to Vaishnava (Krishna-worshipping) communities in the Braj region, where celebrations stretch over roughly a week.', startTime: null },
  { eventId: 'cal_hindu_chaitra_navratri_2026', tradition: 'hindu', title: 'Chaitra Navratri', description: 'Nine nights of devotion to the Goddess, marking the Hindu lunar new year in many regions.', date: '2026-03-19', movable: true, region: 'Observed across India, especially North and West India (e.g. Gujarat, Punjab)', community: 'Hindu festival honoring the goddess Durga/Shakti, observed by Shakta and broader Hindu communities; less publicly prominent in India than the autumn Sharad Navratri, but still widely kept.', startTime: null },
  { eventId: 'cal_hindu_raksha_bandhan_2026', tradition: 'hindu', title: 'Raksha Bandhan', description: 'Sisters tie a protective thread on their brothers’ wrists.', date: '2026-08-28', movable: true, region: 'Most prominent in North India (Punjab, Haryana, Uttar Pradesh, Bihar, Rajasthan); markedly less observed as a distinct festival in much of South India', community: 'Hindu festival celebrating sibling bonds; observed broadly across Hindu communities in North India.', startTime: null },
  { eventId: 'cal_hindu_janmashtami_2026', tradition: 'hindu', title: 'Krishna Janmashtami', description: 'Celebrates the birth of Krishna, marked by fasting, midnight worship, and reenactments of his youth.', date: '2026-09-04', movable: true, region: 'Observed across India, with particular significance in Mathura and Vrindavan (Krishna’s traditional birthplace), and prominent regional traditions in Maharashtra (Dahi Handi) and Gujarat', community: 'Hindu festival especially central to Vaishnava (Krishna-devotee) communities, and to the globally active Hare Krishna (ISKCON) movement.', startTime: 'Krishna is traditionally believed to have been born at midnight, so the culminating puja for many devotees is timed to midnight; the day-long festival otherwise has no single canonical start time.' },
  { eventId: 'cal_hindu_dussehra_2026', tradition: 'hindu', title: 'Sharad Navratri & Dussehra', description: 'Nine nights of devotion to the Goddess culminating in the triumph of good over evil.', date: '2026-10-20', movable: true, region: 'Pan-Indian, with major regionally distinct forms: Durga Puja in West Bengal, Assam, Odisha, and Tripura; Garba/Dandiya Raas in Gujarat; Mysuru Dasara in Karnataka; a separately scheduled Dussehra in the Kullu Valley, Himachal Pradesh', community: 'Underlying theme (victory of good over evil) is shared, but regional theology differs: eastern India centers on Durga’s defeat of the demon Mahishasura (Durga Puja); most of North India centers on Rama’s defeat of Ravana (Ramlila/Dussehra).', startTime: null },
  { eventId: 'cal_hindu_diwali_2026', tradition: 'hindu', title: 'Diwali', description: 'The festival of lights, celebrating the triumph of light over darkness.', date: '2026-11-08', movable: true, region: 'Pan-Indian and South Asian (also Nepal); widely celebrated across the Hindu, Jain, and Sikh diaspora worldwide', community: 'Hindus mark it with regional variation (North India: Rama’s return to Ayodhya; South India: Krishna’s victory over the demon Narakasura). Jains mark the same day as the anniversary of Mahavira’s attainment of moksha. Sikhs observe it as Bandi Chhor Divas, commemorating Guru Hargobind’s release from imprisonment.', startTime: null },
  { eventId: 'cal_buddhist_vesak_2026', tradition: 'buddhist', title: 'Vesak', description: 'Commemorates the Buddha’s birth, enlightenment, and passing — the most sacred day in the Buddhist calendar.', date: '2026-05-31', movable: true, region: 'Most significant in the Theravada Buddhist countries of South and Southeast Asia (Sri Lanka, Thailand, Myanmar, Cambodia, Laos); also observed, on different dates and under different names, by Mahayana and Vajrayana/Tibetan communities', community: 'Theravada Buddhists combine the Buddha’s birth, enlightenment, and death into one Vesak observance on the full moon of the fifth/sixth lunar month. Most Mahayana Buddhists (China, Korea, Vietnam, Japan) instead mark these as separate holidays on different dates. Vajrayana/Tibetan Buddhists combine all three into Saga Dawa Duchen, observed on the full moon of the fourth Tibetan month (usually June).', startTime: null },
  { eventId: 'cal_buddhist_asalha_puja_2026', tradition: 'buddhist', title: 'Asalha Puja (Dharma Day)', description: 'Commemorates the Buddha’s first sermon at Sarnath.', date: '2026-07-29', movable: true, region: 'Theravada Buddhist countries of South and Southeast Asia -- Thailand, Sri Lanka, Myanmar, Cambodia, Laos', community: 'Specific to Theravada Buddhism; commemorates the Buddha’s first sermon and the founding of the monastic Sangha. Not a distinct observance in most Mahayana or Vajrayana practice.', startTime: null },
  { eventId: 'cal_buddhist_vassa_2026', tradition: 'buddhist', title: 'Vassa Begins', description: 'The start of the three-month Rains Retreat for monastics.', date: '2026-07-30', movable: true, region: 'Theravada Buddhist countries of South and Southeast Asia -- Thailand, Sri Lanka, Myanmar, Cambodia, Laos', community: 'A three-month monastic rains retreat observed by Theravada monks and nuns, beginning the day after Asalha Puja; a Theravada-specific institution without a close direct equivalent in most Mahayana or Vajrayana practice.', startTime: null },
  { eventId: 'cal_buddhist_kathina_2026', tradition: 'buddhist', title: 'Kathina', description: 'The robe-offering ceremony marking the end of the Rains Retreat.', date: '2026-10-27', movable: true, region: 'Theravada Buddhist countries of South and Southeast Asia -- Sri Lanka, Thailand, Myanmar, Laos, Cambodia', community: 'Theravada-specific robe-offering ceremony held within one month after Vassa ends; lay devotees present new robes to monastics who completed the rains retreat. Not observed in this form in Mahayana or Vajrayana Buddhism.', startTime: null },
  { eventId: 'cal_chinese_folk_new_year_2026', tradition: 'chinese_folk', title: 'Chinese New Year', description: 'The most important festival in the Chinese calendar — family reunions, ancestor veneration, and red envelopes.', date: '2026-02-17', movable: true, region: 'China (mainland and Taiwan) and Chinese diaspora communities worldwide; related Lunar New Year holidays are also major observances in Korea (Seollal) and Vietnam (Tet)', community: 'Observed across Chinese folk religion, Buddhism, Taoism, and Confucian-rooted family/ancestor customs simultaneously and syncretically -- not exclusive to any one tradition.', startTime: 'The main celebration begins with a family reunion dinner on New Year’s Eve, the evening before (Feb 16, 2026), per traditional lunar-calendar custom.' },
  { eventId: 'cal_chinese_folk_qingming_2026', tradition: 'chinese_folk', title: 'Qingming (Tomb-Sweeping Day)', description: 'A day for honoring ancestors by cleaning their graves and making offerings.', date: '2026-04-04', movable: false, region: 'China, Taiwan, and Chinese diaspora communities worldwide; observed with local variations in Vietnam and among other overseas Chinese communities', community: 'Rooted in Confucian ancestor-veneration and folk-religious grave-sweeping custom, with Taoist ritual elements also present; a public holiday in China and Taiwan.', startTime: null },
  { eventId: 'cal_chinese_folk_dragon_boat_2026', tradition: 'chinese_folk', title: 'Dragon Boat Festival', description: 'Commemorates the poet Qu Yuan, marked by dragon boat races and eating zongzi.', date: '2026-06-19', movable: true, region: 'China, Hong Kong, Taiwan; related festivals observed in Korea (Dano) and Vietnam; widely celebrated among Chinese diaspora communities (Singapore, Malaysia, and beyond)', community: 'A folk observance with Taoist ritual elements (warding off illness/evil at the seasonal midpoint), commemorated broadly across Chinese folk religion; popularly linked to the legend of the poet-statesman Qu Yuan.', startTime: null },
  { eventId: 'cal_chinese_folk_ghost_festival_2026', tradition: 'chinese_folk', title: 'Hungry Ghost Festival', description: 'Believed to be the day spirits of the dead visit the living.', date: '2026-08-27', movable: true, region: 'China, Taiwan, Hong Kong, and Chinese diaspora communities, especially Singapore and Malaysia', community: 'A syncretic observance: Zhongyuan Festival in Taoism and Yulanpen/Ullambana Festival in Buddhism, both incorporating Confucian ancestor-veneration and filial-piety themes; not exclusive to any single tradition.', startTime: null },
  { eventId: 'cal_chinese_folk_mid_autumn_2026', tradition: 'chinese_folk', title: 'Mid-Autumn Festival', description: 'Celebrates the full moon and family reunion, marked by mooncakes and lantern displays.', date: '2026-09-25', movable: true, region: 'China, Taiwan, Hong Kong; related festivals observed in Vietnam, Korea, and Japan; widely celebrated in Chinese diaspora communities worldwide', community: 'A folk-religious/cultural festival with Taoist lunar mythology (Chang’e) and Confucian family-reunion themes; broadly syncretic rather than owned by one tradition.', startTime: null },
];

/** Thrown by mock routes that mirror a real backend access-denied response (403/404) -- carries
 * the same `status`/`access` shape as client.ts's real `ApiError` (see AccessDeniedError in
 * types.ts) without either module importing the other's class. */
class MockApiError extends Error {
  status: number;
  access?: PracticeAccess;
  constructor(status: number, message: string, access?: PracticeAccess) {
    super(message);
    this.status = status;
    this.access = access;
    this.name = 'MockApiError';
  }
}

/** Mirrors backend/app/domain.py's find_prerequisite_practice: the practice one level below
 * `practice` within the same pillar, or null if there is none (level 1, unpillared, or no row
 * fills that slot). */
function findPrerequisitePractice(all: SeedPractice[], practice: SeedPractice): SeedPractice | null {
  if (!practice.pillarId || typeof practice.level !== 'number' || practice.level <= 1) return null;
  return all.find((p) => p.pillarId === practice.pillarId && p.level === practice.level! - 1) ?? null;
}

/** Mirrors backend/app/domain.py's resolve_practice_access exactly -- same prerequisite-before-
 * premium ordering, same "never re-block a replay via prerequisite" rule. Used by getPractices
 * (list annotation), getPracticeAudio (the entitlement gate) and postSession (validation) so all
 * three agree in mock mode too, matching the real backend's three routes. */
function resolvePracticeAccess(
  practice: SeedPractice,
  tier: Me['tier'],
  completedIds: Set<string>,
  all: SeedPractice[],
): PracticeAccess {
  if ((practice.publicationState ?? 'published') === 'unpublished') {
    return 'unpublished';
  }
  const alreadyCompleted = completedIds.has(practice.practiceId);
  if (!alreadyCompleted) {
    const prerequisite = findPrerequisitePractice(all, practice);
    if (prerequisite && !completedIds.has(prerequisite.practiceId)) {
      return 'prerequisite_required';
    }
  }
  if (tier !== 'premium' && typeof practice.level === 'number' && practice.level > FREE_TIER_LEVEL_CAP) {
    return 'premium_required';
  }
  return 'available';
}

/** Mirrors backend/app/domain.py's describe_access_denial -- one place for the exact
 * (status, message) per denied access state, in mock mode too. */
function describeAccessDenial(access: PracticeAccess): [number, string] {
  switch (access) {
    case 'premium_required':
      return [403, 'This level requires Premium.'];
    case 'prerequisite_required':
      return [403, 'Complete the previous level first.'];
    case 'unpublished':
      return [404, "This content isn't available yet."];
    default:
      return [403, "This isn't available."];
  }
}

/** Mirrors backend/app/domain.py's plan_today -- same search order (primary tradition first,
 * pillars by order, first uncompleted level per pillar), same time-budget-then-ignore-it
 * two-pass fallback, same premium-preview/review fallbacks. Deterministic: identical stored
 * state always returns the identical recommendation. */
function planToday(
  traditions: Tradition[],
  primaryTradition: Tradition | null,
  tier: Me['tier'],
  completedIds: string[],
  allPractices: SeedPractice[],
  allPillars: Pillar[],
  timeBudgetMinutes: number | null,
): TodayPlan {
  const completed = new Set(completedIds);
  const orderedTraditions = [
    ...(primaryTradition ? [primaryTradition] : []),
    ...traditions.filter((t) => t !== primaryTradition),
  ];
  if (orderedTraditions.length === 0) {
    return {
      practiceId: null,
      reason: 'none',
      explanation: 'Choose a tradition in Settings to get a personalized recommendation.',
      nextStep: 'Select at least one tradition, then come back to Today.',
    };
  }

  const candidates: SeedPractice[] = [];
  for (const tradition of orderedTraditions) {
    const pillars = allPillars.filter((pl) => pl.tradition === tradition).sort((a, b) => a.order - b.order);
    for (const pillar of pillars) {
      const ladder = allPractices
        .filter((p) => p.pillarId === pillar.pillarId && typeof p.level === 'number')
        .sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
      const next = ladder.find((p) => !completed.has(p.practiceId));
      if (next) candidates.push(next);
    }
  }

  const fitsBudget = (p: SeedPractice) => timeBudgetMinutes == null || p.durationSeconds <= timeBudgetMinutes * 60;
  const access = (p: SeedPractice) => resolvePracticeAccess(p, tier, completed, allPractices);

  // `candidates` is already in tradition/pillar priority order -- the FIRST entry that's both
  // budget-fitting (pass 1) or not (pass 2) and actually "available" wins. Never picked by
  // shortest duration: mirrors backend/app/domain.py's plan_today exactly (a real bug found via
  // live verification -- a shorter practice in a later, lower-priority pillar was jumping ahead
  // of continuing the current one just because it was quicker).
  for (const requireBudgetFit of [true, false]) {
    const pool = candidates.filter((p) => !requireBudgetFit || fitsBudget(p));
    const available = pool.filter((p) => access(p) === 'available');
    if (available.length > 0) {
      const chosen = available[0];
      return {
        practiceId: chosen.practiceId,
        reason: 'next_in_path',
        explanation: `Next in your path: ${chosen.title}.`,
        nextStep: 'Complete this to unlock the next level in this pillar.',
      };
    }
  }

  const premiumLocked = candidates.filter((p) => access(p) === 'premium_required');
  if (premiumLocked.length > 0) {
    const chosen = premiumLocked[0];
    return {
      practiceId: chosen.practiceId,
      reason: 'premium_preview',
      explanation: `Your next step, ${chosen.title}, is a Premium level ($9.99/month).`,
      nextStep: 'Upgrade to Premium to continue this path, or explore another tradition.',
    };
  }

  if (completedIds.length > 0) {
    const lastCompletedId = completedIds[completedIds.length - 1];
    const practice = allPractices.find((p) => p.practiceId === lastCompletedId);
    if (practice) {
      return {
        practiceId: practice.practiceId,
        reason: 'review',
        explanation: `You've completed every released level here — revisit ${practice.title}.`,
        nextStep: 'New levels will appear here as they are released.',
      };
    }
  }

  return {
    practiceId: null,
    reason: 'none',
    explanation: 'No practices are available yet for your selected traditions.',
    nextStep: 'Try adding another tradition in Settings.',
  };
}

const SCHEDULE_TOTAL_DAYS = 30;
const SCHEDULE_NEW_DAY_CUTOFF = 14;

/** Mirrors backend/app/domain.py's generate_thirty_day_schedule exactly -- a pure sequencing of
 * a pillar's already-authored levels, never fabricated content. See that function's docstring
 * for the full reasoning (Development Plan's "Thirty-day flagship structure"). */
function generateThirtyDaySchedule(pillarId: string, allPractices: SeedPractice[]): ScheduleDay[] {
  const ladder = allPractices
    .filter((p) => p.pillarId === pillarId && typeof p.level === 'number')
    .sort((a, b) => (a.level ?? 0) - (b.level ?? 0));

  if (ladder.length === 0) {
    return Array.from({ length: SCHEDULE_TOTAL_DAYS }, (_, i) => ({
      day: i + 1,
      type: 'review' as const,
      practiceId: null,
      note: 'No levels have been authored for this pillar yet.',
    }));
  }

  const schedule: ScheduleDay[] = [];
  const introduced: SeedPractice[] = [];
  let nextNewIndex = 0;

  for (let day = 1; day <= SCHEDULE_TOTAL_DAYS; day++) {
    if (day <= SCHEDULE_NEW_DAY_CUTOFF && nextNewIndex < ladder.length) {
      const practice = ladder[nextNewIndex];
      nextNewIndex++;
      introduced.push(practice);
      const note = day <= 7
        ? `New: ${practice.title} (level ${practice.level}).`
        : `New: ${practice.title} (level ${practice.level}) -- continuing the ladder.`;
      schedule.push({ day, type: 'new', practiceId: practice.practiceId, note });
      continue;
    }

    const practice = introduced[(day - 1) % introduced.length];
    let type: ScheduleDay['type'];
    let note: string;
    if (day <= 7) {
      type = 'review';
      note = `Review: ${practice.title} -- building a feasible rhythm.`;
    } else if (day <= SCHEDULE_NEW_DAY_CUTOFF) {
      type = 'review';
      note = `Review: ${practice.title} -- practicing with confidence.`;
    } else if (day <= 21) {
      type = 'application';
      note = `Apply: bring ${practice.title} into an ordinary moment today.`;
    } else if (day === SCHEDULE_TOTAL_DAYS) {
      type = 'consolidation';
      note = `Consolidate: revisit ${practice.title} and choose what's next.`;
    } else {
      type = 'consolidation';
      note = `Consolidate: ${practice.title} as part of your own routine.`;
    }
    schedule.push({ day, type, practiceId: practice.practiceId, note });
  }

  return schedule;
}

/** Mirrors content.py's _shape_practice: every level is annotated with its resolved access state
 * (never hidden) plus the Work Package 2 content-metadata fields, with the same honest (not
 * fabricated) defaults as the real backend -- "draft" reviewerStatus, empty sourceRefs, null
 * outcome, unless the seed data explicitly sets otherwise (none does today). */
function withAccessState(list: SeedPractice[], tier: Me['tier'], completedIds: Set<string>): Practice[] {
  return list.map((p) => {
    const access = resolvePracticeAccess(p, tier, completedIds, PRACTICES);
    return {
      ...p,
      access,
      // Mirrors content.py's _shape_practice -- only ever populated once access resolves to
      // "available", so a locked level's script can't be read as a paywall bypass here either.
      transcript: access === 'available' ? p.script ?? null : null,
      premiumLocked: access === 'premium_required',
      contentVersion: 1,
      circle: 1,
      publicationState: p.publicationState ?? 'published',
      reviewerStatus: 'draft',
      reviewerName: null,
      sourceRefs: [],
      outcome: null,
      guidanceRequired: false,
    };
  });
}

const BOOKS: Book[] = [
  { bookId: 'b_001', title: 'The Dhammapada', tradition: 'buddhist', format: 'pdf', license: 'public-domain' },
  { bookId: 'b_002', title: 'The Bhagavad Gita', tradition: 'hindu', format: 'pdf', license: 'public-domain' },
  { bookId: 'b_003', title: 'The King James Bible', tradition: 'christian', format: 'epub', license: 'public-domain' },
  { bookId: 'b_004', title: 'The Quran (Rodwell translation)', tradition: 'islamic', format: 'pdf', license: 'public-domain' },
  { bookId: 'b_005', title: 'The Talmud, Selected Writings', tradition: 'jewish', format: 'pdf', license: 'public-domain' },
  { bookId: 'b_006', title: 'Meditations', tradition: 'stoic', format: 'epub', license: 'public-domain' },
  { bookId: 'b_008', title: 'Tao Te Ching', tradition: 'taoist', format: 'txt', license: 'public-domain' },
];

const LEADERBOARD_NAMES = [
  'Wandering Sage', 'Quiet Mountain', 'Still Water', 'Morning Lotus', 'Desert Wind',
  'Gentle River', 'Golden Lantern', 'Silent Bell', 'North Star', 'Open Sky',
  'Even Keel', 'Deep Root', 'Bright Ember', 'Long Breath', 'Kind Fox',
];

/** Selecting a denomination should still surface its parent's general content -- expand the
 * match set to include the parent of any selected subcategory (mirrors the real backend's
 * content.py `_with_parent_fallback`). */
function withParentFallback(traditions: Tradition[]): Set<Tradition> {
  const expanded = new Set(traditions);
  for (const t of traditions) {
    const parent = TRADITION_PARENTS[t];
    if (parent) expanded.add(parent);
  }
  return expanded;
}

/** Mirrors app.domain.is_valid_timezone -- checked against the browser's actual IANA tzdata
 * (via Intl, the JS equivalent of Python's zoneinfo) rather than a hand-maintained allowlist. */
function isValidIanaTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function seededRandom(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
  }
  // Map to [0, 1)
  return ((h >>> 0) % 100000) / 100000;
}

// --- Mock "database" ---------------------------------------------------------------------------

const MOCK_USER_SUB = 'mock-user-0001';

function loadMe(): Me {
  const raw = localStorage.getItem('omnist.mock.me');
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<Me>;
      // Backfill fields added after this record was first saved to localStorage -- mirrors the
      // real backend's _shape_user always defaulting a missing/older field rather than a stored
      // row ever needing a migration. Without this, a browser with pre-existing mock state would
      // silently carry `undefined` for every field introduced since (see pilgrimRank, tier, etc.)
      const backfilled: Me = {
        ...parsed,
        tier: parsed.tier ?? 'free',
        shieldsAvailable: parsed.shieldsAvailable ?? SHIELD_CAP,
        pilgrimRank: parsed.pilgrimRank ?? computePilgrimRank((parsed.completedPracticeIds ?? []).length),
        timezone: parsed.timezone ?? null,
        totalPracticeDays: parsed.totalPracticeDays ?? 0,
        practiceMilestone: parsed.practiceMilestone ?? computePracticeMilestone(parsed.totalPracticeDays ?? 0),
        intent: parsed.intent ?? null,
        experienceLevel: parsed.experienceLevel ?? null,
        timeBudgetMinutes: parsed.timeBudgetMinutes ?? null,
        locale: parsed.locale ?? 'en',
        // Mock mode has no ADMIN_EMAILS allowlist to check against -- always true so the demo
        // build (no real backend) can still show/exercise the admin dashboard.
        isAdmin: parsed.isAdmin ?? true,
      } as Me;
      return backfilled;
    } catch {
      // fall through to default
    }
  }
  const fresh: Me = {
    sub: MOCK_USER_SUB,
    email: 'demo@omnist.app',
    displayName: 'Demo Wanderer',
    traditions: [],
    themePreference: DEFAULT_THEME_ID,
    currentStreak: 0,
    longestStreak: 0,
    lastPracticeDate: null,
    leaderboardOptIn: false,
    gender: null,
    primaryTradition: null,
    completedPracticeIds: [],
    tier: 'free',
    shieldsAvailable: SHIELD_CAP,
    pilgrimRank: 'Seeker',
    timezone: null,
    totalPracticeDays: 0,
    practiceMilestone: 0,
    intent: null,
    experienceLevel: null,
    timeBudgetMinutes: null,
    locale: 'en',
    isAdmin: true,
  };
  saveMe(fresh);
  return fresh;
}

// Mock-only bookkeeping fields layered on top of the public Me contract -- not part of
// types.ts's Me interface since the real GET /api/me never returns backend-internal state like
// this (see user.py's lastReinforcementDate, which similarly never appears in _shape_user).
type StoredMe = Me & {
  lastReinforcementDate?: string | null;
  routinePracticeIds?: string[];
  routineDailyMoment?: DailyMoment | null;
};

function saveMe(me: StoredMe): void {
  localStorage.setItem('omnist.mock.me', JSON.stringify(me));
}

/** Mirrors user.py's _shape_routine: every item carries its CURRENT title/duration/access, and a
 * practiceId that no longer resolves to a real practice is kept (flagged "unpublished") rather
 * than silently dropped -- the caller's own choices are never rewritten without their action. */
function shapeRoutine(me: StoredMe): Routine {
  const practiceIds = me.routinePracticeIds ?? [];
  const completed = new Set(me.completedPracticeIds);
  const byId = new Map(PRACTICES.map((p) => [p.practiceId, p]));

  let totalDurationSeconds = 0;
  const items: RoutineItem[] = practiceIds.map((practiceId) => {
    const practice = byId.get(practiceId);
    if (!practice) {
      return { practiceId, title: null, durationSeconds: null, access: 'unpublished' as PracticeAccess };
    }
    const access = resolvePracticeAccess(practice, me.tier, completed, PRACTICES);
    totalDurationSeconds += practice.durationSeconds;
    return { practiceId, title: practice.title, durationSeconds: practice.durationSeconds, access };
  });

  return { items, dailyMoment: me.routineDailyMoment ?? null, totalDurationSeconds };
}

// --- Private journal storage (mock mode -- localStorage, per-browser, not shared) ----------

function loadJournalEntries(): JournalEntry[] {
  try {
    const raw = localStorage.getItem('omnist.mock.journal');
    return raw ? (JSON.parse(raw) as JournalEntry[]) : [];
  } catch {
    return [];
  }
}

function saveJournalEntries(entries: JournalEntry[]): void {
  localStorage.setItem('omnist.mock.journal', JSON.stringify(entries));
}

// simulate network latency for a more realistic feel
function delay<T>(value: T, ms = 200): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// --- Mock route implementations ------------------------------------------------------------

export const mockApi = {
  async getMe(): Promise<Me> {
    return delay({ ...loadMe() });
  },

  async updateMe(body: UpdateMeBody): Promise<Me> {
    if (body.themePreference !== undefined && !isValidTheme(body.themePreference)) {
      // Mirrors the real backend: PUT /api/me 400s on anything outside the 5 theme ids.
      throw new Error(`Invalid themePreference: ${body.themePreference}`);
    }
    if (body.timezone !== undefined && body.timezone !== null && !isValidIanaTimezone(body.timezone)) {
      // Mirrors app.domain.is_valid_timezone -- rejects anything that isn't a real, loadable
      // IANA zone name (a UTC offset string, a typo, etc.), not just a non-empty string.
      throw new Error(`Invalid timezone: ${body.timezone}`);
    }
    if (body.intent !== undefined && body.intent !== null && !ONBOARDING_INTENTS.includes(body.intent)) {
      throw new Error(`Invalid intent: ${body.intent}`);
    }
    if (body.experienceLevel !== undefined && body.experienceLevel !== null && !EXPERIENCE_LEVELS.includes(body.experienceLevel)) {
      throw new Error(`Invalid experienceLevel: ${body.experienceLevel}`);
    }
    if (
      body.timeBudgetMinutes !== undefined &&
      body.timeBudgetMinutes !== null &&
      (!Number.isInteger(body.timeBudgetMinutes) || body.timeBudgetMinutes < MIN_TIME_BUDGET_MINUTES || body.timeBudgetMinutes > MAX_TIME_BUDGET_MINUTES)
    ) {
      throw new Error(`Invalid timeBudgetMinutes: ${body.timeBudgetMinutes}`);
    }

    const current = loadMe();
    const updated: Me = {
      ...current,
      ...(body.displayName !== undefined ? { displayName: body.displayName } : {}),
      ...(body.traditions !== undefined ? { traditions: body.traditions } : {}),
      ...(body.themePreference !== undefined ? { themePreference: body.themePreference } : {}),
      ...(body.leaderboardOptIn !== undefined ? { leaderboardOptIn: body.leaderboardOptIn } : {}),
      ...(body.gender !== undefined ? { gender: body.gender } : {}),
      ...(body.timezone !== undefined ? { timezone: body.timezone } : {}),
      ...(body.intent !== undefined ? { intent: body.intent } : {}),
      ...(body.experienceLevel !== undefined ? { experienceLevel: body.experienceLevel } : {}),
      ...(body.timeBudgetMinutes !== undefined ? { timeBudgetMinutes: body.timeBudgetMinutes } : {}),
      ...(body.locale !== undefined ? { locale: body.locale } : {}),
    };

    if (body.primaryTradition !== undefined) {
      const effective = body.traditions ?? current.traditions;
      if (body.primaryTradition !== null && !effective.includes(body.primaryTradition)) {
        throw new Error('primaryTradition must be null or one of the selected traditions.');
      }
      updated.primaryTradition = body.primaryTradition;
    } else if (body.traditions !== undefined) {
      // Mirrors the real backend: keep the existing primary if still valid,
      // auto-pick if there's now exactly one tradition, else clear it.
      if (current.primaryTradition && body.traditions.includes(current.primaryTradition)) {
        // unchanged
      } else if (body.traditions.length === 1) {
        updated.primaryTradition = body.traditions[0];
      } else {
        updated.primaryTradition = null;
      }
    }

    saveMe(updated);
    return delay({ ...updated });
  },

  async getTodayQuotes(): Promise<TodayQuotes> {
    const me = loadMe();
    const date = todayUtcDateString();

    if (me.traditions.length === 0) {
      // No traditions selected -> one quote from the full cross-tradition pool.
      const idx = Math.floor(seededRandom(`${me.sub}:${date}:all`) * QUOTES.length);
      return delay({ date, quotes: [QUOTES[idx]] });
    }

    const quotes = me.traditions.map((tradition) => {
      let pool = QUOTES.filter((q) => q.tradition === tradition);
      let seedTag: Tradition = tradition;
      const parent = TRADITION_PARENTS[tradition];
      if (pool.length === 0 && parent) {
        // No quote tagged with this exact denomination yet -- fall back to
        // the parent tradition's quotes instead of showing nothing.
        pool = QUOTES.filter((q) => q.tradition === parent);
        seedTag = parent;
      }
      if (pool.length === 0) return null;
      // Deterministic per (user, date, tradition)
      const idx = Math.floor(seededRandom(`${me.sub}:${date}:${seedTag}`) * pool.length);
      return pool[idx];
    }).filter((q): q is Quote => q !== null);

    return delay({ date, quotes });
  },

  async getPractices(params: { type?: PracticeType; tradition?: Tradition }): Promise<PracticesResponse> {
    const me = loadMe();
    let list = PRACTICES;

    if (params.tradition) {
      // Expand through the parent-fallback even for an explicit single-tradition filter --
      // mirrors backend/app/handlers/content.py's _op_practices fix.
      const matchSet = withParentFallback([params.tradition]);
      list = list.filter((p) => matchSet.has(p.tradition));
    } else if (me.traditions.length > 0) {
      const matchSet = withParentFallback(me.traditions);
      list = list.filter((p) => matchSet.has(p.tradition));
    }

    if (params.type) {
      list = list.filter((p) => p.type === params.type);
    }

    return delay({ practices: withAccessState(list, me.tier, new Set(me.completedPracticeIds)) });
  },

  async getPillars(params: { tradition?: Tradition; all?: boolean }): Promise<PillarsResponse> {
    const me = loadMe();
    let list = PILLARS;

    if (params.tradition) {
      const matchSet = withParentFallback([params.tradition]);
      list = list.filter((pl) => matchSet.has(pl.tradition));
    } else if (!params.all && me.traditions.length > 0) {
      const matchSet = withParentFallback(me.traditions);
      list = list.filter((pl) => matchSet.has(pl.tradition));
    }

    return delay({ pillars: [...list].sort((a, b) => a.order - b.order) });
  },

  async getPracticeAudio(practiceId: string): Promise<PracticeAudio> {
    // Mirrors voice.py's entitlement gate -- mock mode previously let any practiceId through
    // regardless of lock state, which would have hidden the same paywall-bypass gap in local/demo
    // mode that the real backend had until this fix.
    const practice = PRACTICES.find((p) => p.practiceId === practiceId);
    if (!practice) {
      throw new MockApiError(404, 'Practice not found.');
    }
    const me = loadMe();
    const access = resolvePracticeAccess(practice, me.tier, new Set(me.completedPracticeIds), PRACTICES);
    if (access !== 'available') {
      const [status, message] = describeAccessDenial(access);
      throw new MockApiError(status, message, access);
    }

    // No real Polly/S3 pipeline in mock mode. We return a small looping silent WAV bundled as a
    // static asset (public/audio/silence.wav) so the native <audio> element has something real
    // to load and play, in lieu of a real narrated track.
    return delay({
      practiceId,
      url: '/audio/silence.wav',
      expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
      cached: true,
    });
  },

  async getLibrary(tradition?: Tradition): Promise<LibraryResponse> {
    const me = loadMe();
    let list = BOOKS;
    if (tradition) {
      const matchSet = withParentFallback([tradition]);
      list = list.filter((b) => matchSet.has(b.tradition));
    } else if (me.traditions.length > 0) {
      const matchSet = withParentFallback(me.traditions);
      list = list.filter((b) => matchSet.has(b.tradition));
    }
    return delay({ books: list });
  },

  async getLibraryDownload(bookId: string): Promise<LibraryDownload> {
    const book = BOOKS.find((b) => b.bookId === bookId);
    // In mock mode there's no real file; link out to a public-domain source as a stand-in.
    const url = `https://www.gutenberg.org/ebooks/search/?query=${encodeURIComponent(book?.title ?? bookId)}`;
    return delay({
      bookId,
      url,
      expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
    });
  },

  async postSession(body: PostSessionBody): Promise<PostSessionResponse> {
    const me = loadMe();

    // Mirrors user.py's _op_post_session validation -- previously accepted ANY practiceId with
    // no existence/entitlement/prerequisite check, the same gap the real backend had.
    const practice = PRACTICES.find((p) => p.practiceId === body.practiceId);
    if (!practice) {
      throw new MockApiError(404, 'Practice not found.');
    }
    const access = resolvePracticeAccess(practice, me.tier, new Set(me.completedPracticeIds), PRACTICES);
    if (access !== 'available') {
      const [status, message] = describeAccessDenial(access);
      throw new MockApiError(status, message, access);
    }

    // Mock mode has no per-user IANA timezone clock to simulate meaningfully differently from the
    // browser's own -- mirrors the real backend's UTC fallback path (a user who hasn't set
    // `timezone` is evaluated in UTC there too).
    const today = todayUtcDateString();
    const isNewPracticeDay = me.lastPracticeDate !== today;

    // Mirrors app.handlers.user.compute_streak_update: a single missed day (a 2-day gap) is
    // covered by a Streak Shield if one is available -- identical on every tier, never bought.
    let currentStreak = me.currentStreak;
    let shieldsAvailable = me.shieldsAvailable;
    let shieldUsed = false;
    if (!me.lastPracticeDate) {
      currentStreak = 1;
    } else {
      const gap = daysBetween(me.lastPracticeDate, today);
      if (gap === 0) {
        currentStreak = me.currentStreak;
      } else if (gap === 1) {
        currentStreak = me.currentStreak + 1;
      } else if (gap === 2 && me.shieldsAvailable > 0) {
        shieldUsed = true;
        shieldsAvailable = me.shieldsAvailable - 1;
        // streak preserved, unchanged
      } else {
        currentStreak = 1;
      }
    }

    const longestStreak = Math.max(me.longestStreak, currentStreak);

    const completedPracticeIds = me.completedPracticeIds.includes(body.practiceId)
      ? me.completedPracticeIds
      : [...me.completedPracticeIds, body.practiceId];

    const pilgrimRank = computePilgrimRank(completedPracticeIds.length);
    const totalPracticeDays = me.totalPracticeDays + (isNewPracticeDay ? 1 : 0);
    const practiceMilestone = computePracticeMilestone(totalPracticeDays);

    const updated: Me = {
      ...me,
      currentStreak,
      longestStreak,
      lastPracticeDate: today,
      completedPracticeIds,
      shieldsAvailable,
      pilgrimRank,
      totalPracticeDays,
      practiceMilestone,
    };
    saveMe(updated);

    return delay({
      currentStreak,
      longestStreak,
      lastPracticeDate: today,
      completedPracticeIds,
      shieldsAvailable,
      shieldUsed,
      pilgrimRank,
      totalPracticeDays,
      practiceMilestone,
    });
  },

  async postReinforcement(): Promise<ReinforcementResponse> {
    const me = loadMe() as StoredMe;
    // Mirrors user.py's once-per-local-day reinforcement gate -- previously had no limit at all
    // beyond the numeric cap.
    const today = todayUtcDateString();
    if (me.lastReinforcementDate === today) {
      throw new MockApiError(409, 'Already claimed today’s reinforcement.');
    }
    const shieldsAvailable = Math.min(SHIELD_CAP, me.shieldsAvailable + 1);
    saveMe({ ...me, shieldsAvailable, lastReinforcementDate: today });
    return delay({ shieldsAvailable });
  },

  async createCheckout(): Promise<CreateCheckoutResponse> {
    // No real Whop checkout to redirect to in mock mode. Rather than fabricate a fake external
    // URL, this applies the upgrade locally and returns an empty checkoutUrl -- the caller
    // treats that as "already applied, nothing to redirect to" instead of navigating away.
    // Mirrors the real backend's actual effect (tier becomes "premium") without pretending a
    // real payment happened.
    const me = loadMe();
    saveMe({ ...me, tier: 'premium' });
    return delay({ checkoutUrl: '' }, 400);
  },

  async getToday(): Promise<TodayPlan> {
    const me = loadMe();
    return delay(
      planToday(
        me.traditions,
        me.primaryTradition,
        me.tier,
        me.completedPracticeIds,
        PRACTICES,
        PILLARS,
        me.timeBudgetMinutes,
      ),
    );
  },

  async getSchedule(pillarId: string): Promise<ThirtyDaySchedule> {
    return delay({ pillarId, days: generateThirtyDaySchedule(pillarId, PRACTICES) });
  },

  async getRoutine(): Promise<Routine> {
    return delay(shapeRoutine(loadMe() as StoredMe));
  },

  async updateRoutine(body: UpdateRoutineBody): Promise<Routine> {
    if (body.practiceIds !== undefined && body.practiceIds.length > MAX_ROUTINE_PRACTICES) {
      throw new MockApiError(400, `A routine can hold at most ${MAX_ROUTINE_PRACTICES} practices.`);
    }
    if (body.dailyMoment !== undefined && body.dailyMoment !== null && !DAILY_MOMENTS.includes(body.dailyMoment)) {
      throw new MockApiError(400, `Invalid dailyMoment: ${body.dailyMoment}`);
    }
    const me = loadMe() as StoredMe;
    const updated: StoredMe = { ...me };
    if (body.practiceIds !== undefined) updated.routinePracticeIds = body.practiceIds;
    if (body.dailyMoment !== undefined) updated.routineDailyMoment = body.dailyMoment;
    saveMe(updated);
    return delay(shapeRoutine(updated));
  },

  async deleteRoutine(): Promise<Routine> {
    const me = loadMe() as StoredMe;
    saveMe({ ...me, routinePracticeIds: [], routineDailyMoment: null });
    return delay({ items: [], dailyMoment: null, totalDurationSeconds: 0 });
  },

  async listJournalEntries(): Promise<JournalEntriesResponse> {
    return delay({ entries: loadJournalEntries() });
  },

  async createJournalEntry(body: CreateJournalEntryBody): Promise<JournalEntry> {
    if (!body.text || !body.text.trim()) {
      throw new MockApiError(400, 'text is required and cannot be empty.');
    }
    if (body.text.length > JOURNAL_MAX_TEXT_LENGTH) {
      throw new MockApiError(400, `text cannot exceed ${JOURNAL_MAX_TEXT_LENGTH} characters.`);
    }
    const now = new Date().toISOString();
    const entry: JournalEntry = {
      entryId: `mock-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      createdAt: now,
      updatedAt: now,
      text: body.text,
      practiceId: body.practiceId ?? null,
    };
    const entries = loadJournalEntries();
    entries.unshift(entry);
    saveJournalEntries(entries);
    return delay(entry, 150);
  },

  async updateJournalEntry(entryId: string, body: UpdateJournalEntryBody): Promise<JournalEntry> {
    const entries = loadJournalEntries();
    const entry = entries.find((e) => e.entryId === entryId);
    if (!entry) {
      throw new MockApiError(404, 'Journal entry not found.');
    }
    if (body.text !== undefined) {
      if (!body.text.trim()) throw new MockApiError(400, 'text cannot be empty.');
      if (body.text.length > JOURNAL_MAX_TEXT_LENGTH) {
        throw new MockApiError(400, `text cannot exceed ${JOURNAL_MAX_TEXT_LENGTH} characters.`);
      }
      entry.text = body.text;
    }
    if (body.practiceId !== undefined) entry.practiceId = body.practiceId;
    entry.updatedAt = new Date().toISOString();
    saveJournalEntries(entries);
    return delay({ ...entry }, 150);
  },

  async deleteJournalEntry(entryId: string): Promise<{ deleted: boolean }> {
    const entries = loadJournalEntries().filter((e) => e.entryId !== entryId);
    saveJournalEntries(entries);
    return delay({ deleted: true });
  },

  async exportJournal(): Promise<JournalEntriesResponse> {
    return delay({ entries: loadJournalEntries() });
  },

  async getCalendar(params: { tradition?: Tradition }): Promise<CalendarResponse> {
    const me = loadMe();
    let list = CALENDAR_EVENTS;

    if (params.tradition) {
      const matchSet = withParentFallback([params.tradition]);
      list = list.filter((e) => matchSet.has(e.tradition));
    } else if (me.traditions.length > 0) {
      const matchSet = withParentFallback(me.traditions);
      list = list.filter((e) => matchSet.has(e.tradition));
    }

    const sorted = [...list].sort((a, b) => a.date.localeCompare(b.date));
    return delay({ events: sorted.map(withCalendarRichness) });
  },

  async getLeaderboard(limit = 20): Promise<LeaderboardResponse> {
    const me = loadMe();

    const others: LeaderboardEntry[] = LEADERBOARD_NAMES.map((name, i) => ({
      displayName: name,
      currentStreak: Math.max(1, Math.round(seededRandom(`streak:${name}`) * 40) + (LEADERBOARD_NAMES.length - i)),
      rank: 0,
    }));

    const entries = [...others];
    if (me.leaderboardOptIn) {
      entries.push({ displayName: me.displayName || 'You', currentStreak: me.currentStreak, rank: 0 });
    }

    entries.sort((a, b) => b.currentStreak - a.currentStreak);
    entries.forEach((e, i) => {
      e.rank = i + 1;
    });

    return delay({ entries: entries.slice(0, limit) });
  },

  // Mock mode has only one real "account" (localStorage), so the admin dashboard's user list
  // mixes that real demo user in with a handful of synthetic rows (built from
  // LEADERBOARD_NAMES, the same synthetic-name pool the mock leaderboard already uses) purely
  // so the dashboard has something to show -- never persisted, never affects any real state.
  async getAdminUsers(q?: string): Promise<AdminUsersResponse> {
    const me = loadMe();
    const synthetic: AdminUser[] = LEADERBOARD_NAMES.slice(0, 6).map((name, i) => ({
      sub: `mock-${i}`,
      email: `${name.toLowerCase().replace(/\s+/g, '.')}@example.com`,
      displayName: name,
      tier: i % 3 === 0 ? 'premium' : 'free',
      traditions: [ALL_TRADITIONS[i % ALL_TRADITIONS.length]],
      currentStreak: Math.max(0, Math.round(seededRandom(`admin-streak:${name}`) * 30)),
      longestStreak: Math.max(1, Math.round(seededRandom(`admin-longest:${name}`) * 60)),
      lastPracticeDate: null,
      totalPracticeDays: Math.round(seededRandom(`admin-days:${name}`) * 100),
      leaderboardOptIn: i % 2 === 0,
      createdAt: null,
    }));
    const meRow: AdminUser = {
      sub: me.sub,
      email: me.email,
      displayName: me.displayName,
      tier: me.tier,
      traditions: me.traditions,
      currentStreak: me.currentStreak,
      longestStreak: me.longestStreak,
      lastPracticeDate: me.lastPracticeDate,
      totalPracticeDays: me.totalPracticeDays,
      leaderboardOptIn: me.leaderboardOptIn,
      createdAt: null,
    };
    let users = [meRow, ...synthetic];
    if (q) {
      const needle = q.toLowerCase();
      users = users.filter(
        (u) => (u.email || '').toLowerCase().includes(needle) || (u.displayName || '').toLowerCase().includes(needle)
      );
    }
    return delay({ users, total: users.length });
  },

  async updateAdminUser(sub: string, body: { tier: Tier }): Promise<AdminUser> {
    const me = loadMe();
    if (sub === me.sub) {
      saveMe({ ...me, tier: body.tier });
      return delay({
        sub: me.sub, email: me.email, displayName: me.displayName, tier: body.tier,
        traditions: me.traditions, currentStreak: me.currentStreak, longestStreak: me.longestStreak,
        lastPracticeDate: me.lastPracticeDate, totalPracticeDays: me.totalPracticeDays,
        leaderboardOptIn: me.leaderboardOptIn, createdAt: null,
      });
    }
    // Synthetic rows aren't persisted anywhere -- mirror the requested change back so the UI
    // reflects the edit for this session without pretending it's durable.
    return delay({
      sub, email: null, displayName: null, tier: body.tier, traditions: [], currentStreak: 0,
      longestStreak: 0, lastPracticeDate: null, totalPracticeDays: 0, leaderboardOptIn: false,
      createdAt: null,
    });
  },

  async getAdminAnalytics(): Promise<AdminAnalytics> {
    const { users } = await mockApi.getAdminUsers();
    const total = users.length;
    const premium = users.filter((u) => u.tier === 'premium').length;
    const tradition: Record<string, number> = {};
    users.forEach((u) => u.traditions.forEach((t) => { tradition[t] = (tradition[t] ?? 0) + 1; }));
    return delay({
      totalUsers: total,
      premiumUsers: premium,
      freeUsers: total - premium,
      premiumRate: total ? Math.round((premium / total) * 10000) / 10000 : 0,
      usersWithActiveStreak: users.filter((u) => u.currentStreak > 0).length,
      avgCurrentStreak: total ? Math.round((users.reduce((s, u) => s + u.currentStreak, 0) / total) * 100) / 100 : 0,
      avgLongestStreak: total ? Math.round((users.reduce((s, u) => s + u.longestStreak, 0) / total) * 100) / 100 : 0,
      totalDistinctPracticeCompletions: users.reduce((s, u) => s + u.totalPracticeDays, 0),
      leaderboardOptInRate: total ? Math.round((users.filter((u) => u.leaderboardOptIn).length / total) * 10000) / 10000 : 0,
      usersByTradition: tradition,
      usersWithSignupDate: 0,
    });
  },
};
